<template>
  <div class="api-keys">
    <div class="api-keys__header">
      <h2>API Keys</h2>
      <el-button type="primary" @click="createKey" :loading="creating">Create New API Key</el-button>
    </div>

    <div class="api-keys__description">
      <p>API keys allow external applications to create orders and access your services. Keep your API keys secure and never share them publicly.</p>
    </div>

    <div v-if="loading" class="api-keys__loading">
      <i class="el-icon-loading"></i> Loading...
    </div>

    <div v-else-if="apiKeys.length === 0" class="api-keys__empty">
      <p>No API keys yet. Create one to enable third-party integrations.</p>
    </div>

    <div v-else class="api-keys__list">
      <div class="api-key-card" v-for="key in apiKeys" :key="key.id">
        <div class="api-key-card__info">
          <div class="api-key-card__name" v-if="editingId !== key.id">
            {{ key.name }}
            <el-button type="text" size="mini" @click="startEditing(key)" class="edit-btn">
              <i class="el-icon-edit"></i>
            </el-button>
          </div>
          <div class="api-key-card__name-edit" v-else>
            <el-input 
              v-model="editName" 
              size="small" 
              @keyup.enter.native="saveName(key.id)"
              @keyup.esc.native="cancelEditing"
              ref="editInput"
            ></el-input>
            <el-button type="primary" size="mini" @click="saveName(key.id)" :loading="saving">Save</el-button>
            <el-button size="mini" @click="cancelEditing">Cancel</el-button>
          </div>
          <div class="api-key-card__prefix">Key: {{ key.keyPrefix }}...</div>
          <div class="api-key-card__meta">
            <span>Created: {{ formatDate(key.createdAt) }}</span>
            <span v-if="key.lastUsedAt">Last used: {{ formatDate(key.lastUsedAt) }}</span>
            <span v-if="key.expiresAt">Expires: {{ formatDate(key.expiresAt) }}</span>
          </div>
        </div>
        <div class="api-key-card__actions">
          <el-tag :type="key.isActive ? 'success' : 'danger'">
            {{ key.isActive ? 'Active' : 'Inactive' }}
          </el-tag>
          <el-button type="danger" size="small" @click="confirmDelete(key)">Delete</el-button>
        </div>
      </div>
    </div>

    <el-dialog title="API Key Created" :visible.sync="showKeyModal" width="600px" :close-on-click-modal="false">
      <div class="new-key-display">
        <el-alert
          title="Save this key now! It will only be shown once."
          type="warning"
          :closable="false"
          show-icon
        ></el-alert>
        <div class="new-key-value">
          <code>{{ createdKey }}</code>
          <el-button type="text" @click="copyKey">
            <i class="el-icon-copy-document"></i> Copy
          </el-button>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="closeKeyModal">I've Saved My Key</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'ApiKeys',
  data() {
    return {
      apiKeys: [],
      loading: true,
      showKeyModal: false,
      creating: false,
      createdKey: '',
      editingId: null,
      editName: '',
      saving: false
    }
  },
  created() {
    this.fetchApiKeys()
  },
  methods: {
    async fetchApiKeys() {
      this.loading = true
      try {
        const url = process.env.VUE_APP_BACKEND
        const response = await this.$http.get(`${url}/api-keys`)
        this.apiKeys = response.data.apiKeys || []
      } catch (error) {
        this.$message.error('Failed to load API keys')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    async createKey() {
      this.creating = true
      try {
        const url = process.env.VUE_APP_BACKEND
        const keyNumber = this.apiKeys.length + 1
        const response = await this.$http.post(`${url}/api-keys`, {
          name: `API_Key_${keyNumber}`,
          expiresAt: null
        })
        this.createdKey = response.data.apiKey
        this.showKeyModal = true
        await this.fetchApiKeys()
      } catch (error) {
        this.$message.error('Failed to create API key')
        console.error(error)
      } finally {
        this.creating = false
      }
    },
    startEditing(key) {
      this.editingId = key.id
      this.editName = key.name
      this.$nextTick(() => {
        if (this.$refs.editInput) {
          this.$refs.editInput.focus()
        }
      })
    },
    cancelEditing() {
      this.editingId = null
      this.editName = ''
    },
    async saveName(id) {
      if (!this.editName.trim()) {
        this.$message.warning('Name cannot be empty')
        return
      }
      this.saving = true
      try {
        const url = process.env.VUE_APP_BACKEND
        await this.$http.put(`${url}/api-keys/${id}`, {
          name: this.editName
        })
        this.$message.success('Name updated')
        this.cancelEditing()
        await this.fetchApiKeys()
      } catch (error) {
        this.$message.error('Failed to update name')
        console.error(error)
      } finally {
        this.saving = false
      }
    },
    confirmDelete(key) {
      this.$confirm(`Are you sure you want to delete the API key "${key.name}"? This action cannot be undone.`, 'Delete API Key', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.deleteKey(key.id)
      }).catch(() => {})
    },
    async deleteKey(id) {
      try {
        const url = process.env.VUE_APP_BACKEND
        await this.$http.delete(`${url}/api-keys/${id}`)
        this.$message.success('API key deleted')
        await this.fetchApiKeys()
      } catch (error) {
        this.$message.error('Failed to delete API key')
        console.error(error)
      }
    },
    copyKey() {
      navigator.clipboard.writeText(this.createdKey).then(() => {
        this.$message.success('API key copied to clipboard')
      }).catch(() => {
        this.$message.error('Failed to copy')
      })
    },
    closeKeyModal() {
      this.showKeyModal = false
      this.createdKey = ''
    },
    formatDate(date) {
      return moment(date).format('MMM D, YYYY')
    }
  }
}
</script>

<style lang="scss" scoped>
.api-keys {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h2 {
      margin: 0;
    }
  }

  &__description {
    background: #f5f7fa;
    padding: 15px;
    border-radius: 4px;
    margin-bottom: 20px;

    p {
      margin: 0;
      color: #606266;
    }
  }

  &__loading, &__empty {
    text-align: center;
    padding: 40px;
    color: #909399;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
}

.api-key-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #fff;

  &__info {
    flex: 1;
  }

  &__name {
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 5px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    .edit-btn {
      padding: 0;
      color: #909399;
      
      &:hover {
        color: #409eff;
      }
    }
  }

  &__name-edit {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 5px;
    
    .el-input {
      width: 200px;
    }
  }

  &__prefix {
    font-family: monospace;
    color: #909399;
    margin-bottom: 5px;
  }

  &__meta {
    font-size: 12px;
    color: #909399;

    span {
      margin-right: 15px;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.new-key-display {
  .new-key-value {
    margin-top: 20px;
    padding: 15px;
    background: #f5f7fa;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    
    code {
      font-size: 14px;
      word-break: break-all;
      flex: 1;
    }
  }
}
</style>
